import React, { useState } from 'react';
import { AlignLeft } from 'lucide-react';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface MedioPago {
  /** Nombre del medio de pago. Ej: "Efectivo", "Yape" */
  nombre: string;
  /** Monto total en este medio */
  monto: number;
  /** Ícono opcional (ReactNode). Si no se pasa, muestra las iniciales */
  icono?: React.ReactNode;
}

export interface DesgloseMediosPagoProps {
  /** Lista de medios de pago para ingresos */
  ingresos: MedioPago[];
  /** Lista de medios de pago para egresos */
  egresos: MedioPago[];
  /** Tab inicial. Default: 'ingresos' */
  defaultTab?: 'ingresos' | 'egresos';
  /** Clase adicional para el contenedor raíz */
  className?: string;
}

type Tab = 'ingresos' | 'egresos';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatMonto = (monto: number): string =>
  `S/ ${monto.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const getInicialesIcon = (nombre: string): string =>
  nombre
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

const calcularPorcentaje = (monto: number, total: number): number =>
  total === 0 ? 0 : Math.round((monto / total) * 100);

// ─── Subcomponente: fila de medio de pago ────────────────────────────────────

interface MedioRowProps {
  medio: MedioPago;
  total: number;
  tipo: Tab;
}

const MedioRow: React.FC<MedioRowProps> = ({ medio, total, tipo }) => {
  const pct = calcularPorcentaje(medio.monto, total);
  const esIngreso = tipo === 'ingresos';

  return (
    <div className="flex items-center justify-between px-4 py-2 hover:bg-surface-hover transition-colors">
      {/* Ícono + nombre */}
      <div className="flex items-center gap-2.5 min-w-0">
        <div
          className={[
            'w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0',
            'text-[11px] font-medium',
            esIngreso ? 'bg-info-bg text-info' : 'bg-error-bg text-error',
          ].join(' ')}
          aria-hidden="true"
        >
          {medio.icono ?? getInicialesIcon(medio.nombre)}
        </div>
        <div className="flex flex-col min-w-0">
          <span className="text-[12px] font-medium text-primary truncate leading-tight">
            {medio.nombre}
          </span>
          <span className="text-[10px] text-tertiary leading-tight">{pct}%</span>
        </div>
      </div>

      {/* Monto + barra */}
      <div className="flex flex-col items-end gap-1 ml-3 flex-shrink-0">
        <span className="text-[12px] font-medium text-primary whitespace-nowrap">
          {formatMonto(medio.monto)}
        </span>
        <div className="w-12 h-[3px] bg-surface-2 rounded-full overflow-hidden">
          <div
            className={[
              'h-full rounded-full transition-all duration-500',
              esIngreso ? 'bg-info' : 'bg-error',
            ].join(' ')}
            style={{ width: `${pct}%` }}
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${pct}% del total`}
          />
        </div>
      </div>
    </div>
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────

export const DesgloseMediosPago: React.FC<DesgloseMediosPagoProps> = ({
  ingresos,
  egresos,
  defaultTab = 'ingresos',
  className = '',
}) => {
  const [tab, setTab] = useState<Tab>(defaultTab);

  React.useEffect(() => {
    setTab(defaultTab);
  }, [defaultTab]);

  const lista = tab === 'ingresos' ? ingresos : egresos;
  const total = lista.reduce((acc, m) => acc + m.monto, 0);

  return (
    <div className={['flex flex-col h-full', className].join(' ')}>
      {/* ── Tabs ── */}
      <div className="flex gap-1.5 px-4 py-3 flex-shrink-0">
        <button
          type="button"
          onClick={() => setTab('ingresos')}
          className={[
            'flex-1 text-[11px] font-medium py-1.5 rounded-lg transition-colors',
            tab === 'ingresos'
              ? 'bg-info-bg text-info'
              : 'bg-surface-hover text-tertiary hover:text-secondary',
          ].join(' ')}
        >
          + Ingresos
        </button>
        <button
          type="button"
          onClick={() => setTab('egresos')}
          className={[
            'flex-1 text-[11px] font-medium py-1.5 rounded-lg transition-colors',
            tab === 'egresos'
              ? 'bg-error-bg text-error'
              : 'bg-surface-hover text-tertiary hover:text-secondary',
          ].join(' ')}
        >
          − Egresos
        </button>
      </div>

      {/* ── Lista ── */}
      <div className="flex-1 overflow-y-auto py-2 min-h-0">
        {lista.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-2 text-tertiary py-8">
            <AlignLeft size={20} />
            <span className="text-[12px]">Sin movimientos</span>
          </div>
        ) : (
          lista.map((medio) => (
            <MedioRow key={`${medio.nombre}-${medio.monto}`} medio={medio} total={total} tipo={tab} />
          ))
        )}
      </div>

      {/* ── Total ── */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-muted flex-shrink-0">
        <span className="text-[11px] font-medium text-tertiary uppercase tracking-wider">
          Total
        </span>
        <span className="text-ui-base font-sans font-semibold text-primary">
          {formatMonto(total)}
        </span>
      </div>
    </div>
  );
};

DesgloseMediosPago.displayName = 'DesgloseMediosPago';


